import { getRouteApi, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Column } from '~/app/AppShell'
import { BackLink } from '~/components/BackLink'
import { ItemIcon } from '~/components/ItemIcon'
import { Section, Unknown } from '~/components/Section'
import { type DisplayIndex, loadDataset, loadDisplayIndex } from '~/lib/data'
import { useDocumentTitle } from '~/lib/head'
import { floorRange, type MineFloors } from '~/lib/labels'
import { iconKeyFor } from '~/lib/search'

const route = getRouteApi('/monster/$id')

/**
 * One monster: where it lives, what it drops, and what a fight costs.
 *
 * The numbers come from the game's own variant tables (`fiddle/monsters/`),
 * joined in the pipeline — hp, contact damage, the essence a kill pays, and a
 * coin range. A perk-gated drop line says so out loud: a 5% chance that is 0%
 * without Friend-Shaped is not a 5% chance.
 */

interface MonsterDrop {
  item_id: string
  chance: number | null
  quantity: { min: number; max: number } | null
  requires_perk: string | null
}

interface MonsterRecord {
  id: string
  name: string
  icon_key: string | null
  hp: number | null
  damage: number | null
  essence: number | null
  coins: { min: number; max: number } | null
  biome_ids: string[]
  drops: MonsterDrop[]
  /** Separately rolled — the Rock Stack's guaranteed diamonds on a full break. */
  super_drops: MonsterDrop[]
  data_gaps: string[]
}

function DropList({ drops, index }: { drops: MonsterDrop[]; index: DisplayIndex }) {
  return (
    <ul className="flex flex-col divide-y divide-rule border-rule border-y">
      {[...drops]
        .sort((a, b) => (b.chance ?? 0) - (a.chance ?? 0))
        .map((drop) => (
          <li key={drop.item_id}>
            <Link
              to="/item/$id"
              params={{ id: drop.item_id }}
              className="flex items-center gap-3 py-2 transition-colors hover:bg-sunk"
            >
              <ItemIcon
                iconKey={iconKeyFor(drop.item_id, index[drop.item_id])}
                name={index[drop.item_id]?.n ?? drop.item_id}
                size="sm"
              />
              <span className="min-w-0 flex-1 truncate text-ink text-sm">
                {index[drop.item_id]?.n ?? drop.item_id.replace(/_/g, ' ')}
                {drop.quantity !== null && drop.quantity.max > 1 && (
                  <span data-numeral className="text-ink-faint text-xs">
                    {' '}
                    ×
                    {drop.quantity.min === drop.quantity.max
                      ? drop.quantity.max
                      : `${drop.quantity.min}–${drop.quantity.max}`}
                  </span>
                )}
              </span>
              {drop.requires_perk === 'friend_shaped' && (
                <span
                  className="shrink-0 rounded-tile px-1.5 py-0.5 text-[0.625rem]"
                  style={{ color: 'var(--locked)', border: '1px solid var(--locked)' }}
                >
                  needs Friend-Shaped
                </span>
              )}
              {/* A null chance is unknown, and unknown never renders as a
                  number — 0% would read as "never drops". */}
              {drop.chance === null ? (
                <span className="unverified shrink-0 rounded-tile px-1.5 py-0.5 text-[0.625rem]">
                  chance unknown
                </span>
              ) : (
                <span data-numeral className="shrink-0 text-ink-faint text-xs">
                  {Math.round(drop.chance * 100)}%
                </span>
              )}
            </Link>
          </li>
        ))}
    </ul>
  )
}

export function BestiaryRoute() {
  const { id } = route.useParams()
  const [state, setState] = useState<{
    monster: MonsterRecord | null
    biomes: Map<string, { name: string; locationId: string | null; floors: MineFloors }>
    index: DisplayIndex
    loading: boolean
  }>({ monster: null, biomes: new Map(), index: {}, loading: true })

  useEffect(() => {
    let live = true
    Promise.all([
      loadDataset<MonsterRecord>('monsters'),
      loadDataset<{
        id: string
        name: string
        location_id: string | null
        floors: MineFloors
      }>('mines'),
      loadDisplayIndex(),
    ])
      .then(([monsters, mines, index]) => {
        if (!live) return
        setState({
          monster: monsters.find((m) => m.id === id) ?? null,
          biomes: new Map(
            mines.map((m) => [m.id, { name: m.name, locationId: m.location_id, floors: m.floors }]),
          ),
          index,
          loading: false,
        })
      })
      .catch(() => live && setState((s) => ({ ...s, loading: false })))
    return () => {
      live = false
    }
  }, [id])

  const { monster, biomes, index, loading } = state
  useDocumentTitle(monster?.name ?? null)

  if (loading) {
    return (
      <Column>
        <p className="text-ink-mute text-sm">Loading…</p>
      </Column>
    )
  }

  if (monster === null) {
    return (
      <Column>
        <h1 className="text-2xl">Not found</h1>
        <p className="mt-1 text-ink-mute text-sm">
          Nothing here goes by “{id.replace(/_/g, ' ')}”.{' '}
          <Link to="/search" className="underline decoration-rule underline-offset-4">
            Search instead
          </Link>
          .
        </p>
      </Column>
    )
  }

  return (
    <Column>
      <BackLink />
      <header className="flex items-center gap-3">
        <ItemIcon
          iconKey={monster.icon_key ?? `monster/${monster.id}`}
          name={monster.name}
          size="lg"
        />
        <div className="min-w-0">
          <h1 className="truncate text-2xl">{monster.name}</h1>
          <p className="mt-0.5 text-ink-mute text-sm">
            {monster.biome_ids.length === 0
              ? 'home unknown'
              : monster.biome_ids.map((b, i) => {
                  const biome = biomes.get(b)
                  const label = biome?.name ?? b.replace(/_/g, ' ')
                  return (
                    <span key={b}>
                      {i > 0 && ' · '}
                      {biome?.locationId == null ? (
                        label
                      ) : (
                        <Link
                          to="/place/$id"
                          params={{ id: biome.locationId }}
                          className="underline decoration-rule underline-offset-4 hover:text-ink"
                        >
                          {label}
                        </Link>
                      )}
                      {/* "The Lava Caves" does not say how far down it is, and
                          for a monster's home that is the whole question. */}
                      {biome !== undefined && (
                        <span data-numeral className="text-ink-faint">
                          {' '}
                          ({floorRange(biome.floors)})
                        </span>
                      )}
                    </span>
                  )
                })}
          </p>
        </div>
      </header>

      <Section title="Drops">
        {monster.drops.length === 0 ? (
          <Unknown>No drops recorded.</Unknown>
        ) : (
          <DropList drops={monster.drops} index={index} />
        )}
        {monster.super_drops.length > 0 && (
          <>
            {/* A second, separately-rolled table — folding it in would state a
                100% diamond chance on an ordinary kill. */}
            <h3 className="mt-3 font-display font-semibold text-ink text-sm">
              For breaking it completely
            </h3>
            <DropList drops={monster.super_drops} index={index} />
          </>
        )}
      </Section>

      <Section title="In a fight">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-ink-mute">Health</dt>
          <dd className="text-ink">
            {monster.hp === null ? (
              <Unknown>not recorded</Unknown>
            ) : (
              <span data-numeral>{monster.hp}</span>
            )}
          </dd>
          <dt className="text-ink-mute">Damage</dt>
          <dd className="text-ink">
            {monster.damage === null ? (
              <Unknown>not recorded</Unknown>
            ) : (
              <span data-numeral>{monster.damage}</span>
            )}
          </dd>
          <dt className="text-ink-mute">Essence</dt>
          <dd className="text-ink">
            {monster.essence === null ? (
              <Unknown>not recorded</Unknown>
            ) : (
              <span data-numeral>{monster.essence}</span>
            )}
          </dd>
          {monster.coins !== null && (
            <>
              <dt className="text-ink-mute">Coins</dt>
              <dd className="text-ink" data-numeral>
                {monster.coins.min === monster.coins.max
                  ? monster.coins.min
                  : `${monster.coins.min}–${monster.coins.max}`}
              </dd>
            </>
          )}
        </dl>
      </Section>
    </Column>
  )
}
